import React, { useContext, useEffect, useState } from 'react'

import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'

import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner'
import RefreshIcon from '@mui/icons-material/Refresh'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import API from '../../../services/API'
import SuccessModal from '../../../components/Modals/Success'
import { GlobalContext } from '../../../App'
import { useNavigate } from 'react-router-dom'
import QRCodeReader from '../../../helpers/QRCodeReader'

const categories = [
  'Medical Device',
  'Medical Bag',
  'Moving and Handling',
  'Medical Gas',
  'IT Equipment',
  'Other',
]
const medDevCats = [
  'Defibrillator / Monitor',
  'AED',
  'Suction Unit',
  'Ventilator',
  'Thermometer',
  'Pulse Oximeter',
  'Blood Glucose Monitor',
  'Blood Pressure Machine',
  'Other',
]
const medBagCats = [
  'Paramedic Bag',
  'First Aid Bag',
  'Wound Closure Bag',
  'Obs Kit',
  'Oxygen',
  'Entonox',
  'Other',
]

const movHanCats = ['Carry Chair', 'Stretcher', 'KED', 'Other']
const itEquiCats = ['iPad', 'Radio', 'Printer', 'Phone', 'Other']
const medGasCats = ['Oxygen', 'Entonox', 'Other']

const AddNewAsset = ({ existingAsset, onClose }) => {
  const [categoryOptions, setCategoryOptions] = useState(categories)
  const [assetCustomSubCategories, setAssetCustomSubCategories] = useState([])
  const [subCategoryOptions, setSubCategoryOptions] = useState([])
  const [qrScannerOpen, setQrScannerOpen] = useState(false)
  const [assetTag, setAssetTag] = useState(existingAsset?.assetID ?? '')
  const [category, setCategory] = useState(existingAsset?.category ?? '')
  const [subCategory, setSubCategory] = useState(
    existingAsset?.subCategory ?? '',
  )
  const [requiresServicing, setRequiresServicing] = useState(
    existingAsset?.requiresServicing ?? false,
  )
  const [makeReadyLoadList, setMakeReadyLoadList] = useState(
    existingAsset?.makeReadyLoadList?._id ?? null,
  )
  const [manufacturer, setManufacturer] = useState(
    existingAsset?.manufacturer ?? '',
  )
  const [brand, setBrand] = useState(existingAsset?.brand ?? '')
  const [model, setModel] = useState(existingAsset?.model ?? '')
  const [serialNumber, setSerialNumber] = useState(
    existingAsset?.serialNumber ?? '',
  )
  const [notes, setNotes] = useState(existingAsset?.notes ?? '')
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [assetIDDuplicateError, setAssetIDDuplicateError] = useState(false)
  const [makeReadyLoadListOptions, setMakeReadyLoadListOptions] = useState([])
  const { setToast } = useContext(GlobalContext)
  const navigate = useNavigate()

  const fetchMakeReadyLoadListOptions = () => {
    API.get('/settings/makereadyloadlists').then((res) => {
      if (res.status === 200) {
        setMakeReadyLoadListOptions(res.data)
      }
    })
  }

  useEffect(() => {
    fetchMakeReadyLoadListOptions()
  }, [])

  const subCatAr = () => {
    switch (category) {
      case '':
        return []
      case 'Other':
        return assetCustomSubCategories
          .filter((x) => x.category === 'Other')
          .map((x) => x.name)
      case 'Medical Device':
        return [
          ...medDevCats,
          ...assetCustomSubCategories
            .filter((x) => x.category === 'Medical Device')
            .map((x) => x.name),
        ]
      case 'Medical Bag':
        return [
          ...medBagCats,
          ...assetCustomSubCategories
            .filter((x) => x.category === 'Medical Bag')
            .map((x) => x.name),
        ]
      case 'Moving and Handling':
        return [
          ...movHanCats,
          ...assetCustomSubCategories
            .filter((x) => x.category === 'Moving and Handling')
            .map((x) => x.name),
        ]
      case 'Medical Gas':
        return [
          ...medGasCats,
          ...assetCustomSubCategories
            .filter((x) => x.category === 'Medical Gas')
            .map((x) => x.name),
        ]
      case 'IT Equipment':
        return [
          ...itEquiCats,
          ...assetCustomSubCategories
            .filter((x) => x.category === 'IT Equipment')
            .map((x) => x.name),
        ]
      default:
        return assetCustomSubCategories
          .filter((x) => x.category === category)
          .map((x) => x.name)
    }
  }

  useEffect(() => {
    API.get(
      '/settings?select=assetCustomCategories%20assetCustomSubCategories',
    ).then((res) => {
      if (res.status === 200) {
        const facc = (res.data?.assetCustomCategories ?? []).filter(Boolean)
        const facs = (res.data?.assetCustomSubCategories ?? []).filter(
          (x) => x.name,
        )
        setCategoryOptions((prev) => [...prev, ...facc])
        setAssetCustomSubCategories(facs)
      }
    })
  }, [])

  const handleSubmit = (e) => {
    e.preventDefault()
    if (existingAsset) {
      API.put('/asset/' + existingAsset._id, {
        assetID: assetTag,
        category,
        subCategory,
        requiresServicing,
        makeReadyLoadList,
        manufacturer,
        brand,
        model,
        serialNumber,
        notes,
        _id: existingAsset?._id,
      }).then((res) => {
        if (res.status === 200) {
          if (existingAsset?.assetID !== assetTag) {
            navigate('/asset-manager/' + assetTag)
          }
          onClose && onClose()
        } else if (res.status === 400 && res?.data?.duplicateError) {
          setAssetIDDuplicateError(true)
          setToast({
            message: res.data.duplicateError,
            severity: 'warning',
          })
        }
      })
    } else {
      console.log('makeReadyLoadList', makeReadyLoadList)
      API.post('/asset', {
        assetID: assetTag,
        category,
        subCategory,
        manufacturer,
        brand,
        model,
        makeReadyLoadList,
        requiresServicing,
        serialNumber,
        notes,
      }).then((res) => {
        if (res.status === 200) {
          navigate('/asset-manager/' + assetTag)
        } else if (res.status === 400 && res?.data?.duplicateError) {
          setAssetIDDuplicateError(true)
          setToast({
            message: res.data.duplicateError,
            severity: 'warning',
          })
        }
      })
    }
  }

  const categoryValue = categoryOptions.includes(category) ? category : null
  const subCategoryValue = subCategoryOptions.includes(subCategory)
    ? subCategory
    : null

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box
        component={Paper}
        padding={1}
        display="flex"
        flexDirection="column"
        gap={1}
      >
        <Typography variant="title2">
          {existingAsset ? 'Edit' : 'Add New'} Asset
        </Typography>
        <Box display="flex" flex={1} gap={1} alignItems="center">
          <QrCodeScannerIcon
            color="error"
            sx={{ cursor: 'pointer' }}
            onClick={() => setQrScannerOpen(true)}
          />
          <TextField
            label="Asset ID"
            required
            fullWidth
            value={assetTag}
            onChange={(e) => setAssetTag(e.target.value.replace(/\s+/g, ''))}
            autoComplete="off"
            error={assetIDDuplicateError}
            helperText={assetIDDuplicateError && 'This ID already exists'}
          />
          <QRCodeReader
            open={qrScannerOpen}
            onClose={() => setQrScannerOpen(false)}
            onScan={(result) => {
              setAssetTag(result)
              setQrScannerOpen(false)
            }}
          />
        </Box>
        <Autocomplete
          value={categoryValue}
          inputValue={category}
          onChange={(e, newValue) => {
            setCategory(newValue ?? '')
            if (subCategory) setSubCategory('')
          }}
          onInputChange={(e, newValue) => {
            setCategory(newValue ?? '')
            if (subCategory) setSubCategory('')
          }}
          freeSolo
          options={categoryOptions}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              label="Category"
              helperText="FreeText your own if required"
            />
          )}
        />

        <Autocomplete
          value={subCategoryValue}
          inputValue={subCategory}
          onChange={(e, newValue) => setSubCategory(newValue ?? '')}
          onInputChange={(e, newValue) => setSubCategory(newValue ?? '')}
          options={subCatAr()}
          freeSolo
          disabled={!category}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              label="Sub-Category"
              helperText="FreeText your own if required"
            />
          )}
        />
        {/* Requires Servicing */}
        <FormControlLabel
          label="This asset requires servicing"
          control={
            <Switch
              checked={requiresServicing}
              onChange={(e) => setRequiresServicing(e.target.checked)}
              color="success"
            />
          }
        />
        <Stack direction="row" gap={1} alignItems="flex-start">
          <Autocomplete
            fullWidth
            value={
              makeReadyLoadListOptions.find(
                (option) => option._id === makeReadyLoadList,
              ) || null
            }
            onChange={(e, newValue) => {
              setMakeReadyLoadList(newValue?._id || null)
            }}
            options={makeReadyLoadListOptions}
            getOptionLabel={(option) => `${option.title} (${option.type})`}
            isOptionEqualToValue={(opt, val) => opt._id === val?._id}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label="Make Ready Load List (Optional)"
                helperText="Select a list that's specific to this device (i.e. Cardiac Monitor). Staff will then use this load list when checking this asset."
              />
            )}
          />
          <Tooltip title="Refresh list">
            <IconButton onClick={fetchMakeReadyLoadListOptions} sx={{ mt: 1 }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
        <Link
          component="button"
          type="button"
          variant="caption"
          onClick={() => window.open('/make-ready-load-lists/new', '_blank')}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          Don&apos;t see the right list? Create one <OpenInNewIcon fontSize="inherit" />
        </Link>
        <TextField
          value={manufacturer}
          onChange={(e) => setManufacturer(e.target.value)}
          label="Manufacturer"
          autoComplete="off"
        />
        <TextField
          value={brand}
          onChange={(e) => setBrand(e.target.value)}
          label="Brand"
          autoComplete="off"
        />
        <TextField
          value={model}
          onChange={(e) => setModel(e.target.value)}
          label="Model"
          autoComplete="off"
        />
        <TextField
          value={serialNumber}
          onChange={(e) => setSerialNumber(e.target.value)}
          label="Serial Number"
          autoComplete="off"
        />
        <TextField
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          label="Notes"
          autoComplete="off"
          multiline
          minRows={3}
        />
        <Button type="submit" fullWidth color="success" variant="contained">
          Submit
        </Button>
        <SuccessModal
          open={submitSuccess}
          countdown={10}
          newUrl="/asset-manager/add-new"
        />
      </Box>
    </Box>
  )
}

export default AddNewAsset
